import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function HourlySmoothChart({
  data = [],
  lineColor = '#3b82f6',
  fillColor = 'rgba(59,130,246,0.15)',
  backgroundColor = '#0f172a',
  strokeWidth = 2,
  padding = 0,
  containerHeight = 235
}) {
  const wrapperRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredIndex, setHoveredIndex] = useState(null); // Stan dla aktywnego punktu

  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const { width, height } = dimensions;

  // Wstępne obliczenie koordynatów X i Y dla wszystkich punktów
  const points = React.useMemo(() => {
    if (width === 0 || height === 0 || data.length === 0) return [];
    
    const maxValue = 100; // Oś Y zawsze do 100, możesz to zmienić na Math.max(...data)
    const stepX = (width - padding * 2) / (data.length - 1 || 1);

    return data.map((value, i) => ({
      x: padding + i * stepX,
      y: height - padding - (value.amount / maxValue) * (height - padding * 2),
      value: value.amount,
    }));
  }, [data, width, height, padding]);

  // Budowa krzywej Beziera na podstawie wyliczonych punktów
  const buildSmoothPath = (pts) => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0].x} ${pts[0].y}`;

    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i];
      const p1 = pts[i + 1];
      const cx = (p0.x + p1.x) / 2;
      d += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`;
    }
    return d;
  };

  if (width === 0 || height === 0) {
    return (
      <div
        ref={wrapperRef}
        style={{ backgroundColor, width: '100%', height: '300px', minHeight: '200px' }}
      />
    );
  }

  const path = buildSmoothPath(points);
  const areaPath = path ? `${path} L ${width - padding} ${height - padding} L ${padding} ${height - padding} Z` : '';

  return (
    <div
      ref={wrapperRef}
      style={{ backgroundColor, width: '100%', height: containerHeight, position: 'relative' }}
    >
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fillColor} />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>

        {/* Tło wykresu */}
        {areaPath && (
          <motion.path
            d={areaPath}
            fill="url(#chartFill)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
        )}

        {/* Linia wykresu */}
        {path && (
          <motion.path
            d={path}
            fill="none"
            stroke={lineColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          />
        )}

        {/* Interaktywne punkty */}
        {points.length > 1 && points.map((point, i) => {
          const isHovered = hoveredIndex === i;

          return (
            <g key={i}>
              {/* Przezroczyste, większe koło - łapie eventy myszki (ułatwia trafienie) */}
              <circle
                cx={point.x}
                cy={point.y}
                r={16} // Duży obszar najechania
                fill="transparent"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ cursor: 'pointer', outline: 'none' }}
              />
              
              {/* Wizualne koło (kropka wykresu) - animowane */}
              <motion.circle
                cx={point.x}
                cy={point.y}
                initial={{ r: 4, fill: lineColor, strokeWidth: 0 }}
                animate={{
                  r: isHovered ? 6 : 4, // Zmiana rozmiaru
                  fill: isHovered ? '#ffffff' : lineColor, // Zmiana koloru na biały przy najechaniu
                  strokeWidth: isHovered ? 2 : 0,
                }}
                stroke={lineColor}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: 'none' }} // Zapobiega konfliktom z niewidzialnym kołem powyżej
              />
            </g>
          );
        })}
      </svg>

      {/* Dymek (Tooltip) */}
      <AnimatePresence>
        {hoveredIndex !== null && points[hoveredIndex] && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              left: points[hoveredIndex].x,
              top: points[hoveredIndex].y - 35, // Pozycja nad punktem
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--bg-modal-color)',
              border: `1px solid ${lineColor}`,
              color: 'var(--maintext-color)',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              pointerEvents: 'none', // Dymek nie blokuje myszki
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
              zIndex: 10,
            }}
          >
            {/* Tekst wyświetlany w dymku */}
            <p>Інтеракції: {points[hoveredIndex].value.toFixed(1)}</p>
            <p>Година: {data[hoveredIndex].hour} - {data[hoveredIndex].hour+1}</p>
            

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}